import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, Eye, EyeOff, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";

const FIREBASE_ERRORS = {
  "auth/user-not-found": "No account with that email.",
  "auth/wrong-password": "Incorrect password.",
  "auth/invalid-credential": "Invalid email or password.",
  "auth/invalid-email": "Invalid email address.",
  "auth/user-disabled": "This account has been disabled.",
  "auth/too-many-requests": "Too many attempts. Try again later.",
};

export default function Login() {
  const { login, googleLogin, user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [email, setE] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setL] = useState(false);

  // Redirect already-authenticated users (covers Google redirect return)
  useEffect(() => {
    if (!authLoading && user) nav("/dashboard", { replace: true });
  }, [user, authLoading, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setL(true);
    try {
      await login(email, pw);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (err) {
      toast.error(FIREBASE_ERRORS[err?.code] || "Login failed");
    } finally { setL(false); }
  };

  const google = async () => {
    try {
      const result = await googleLogin();
      if (result?.user) nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(FIREBASE_ERRORS[err?.code] || "Google sign-in failed");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-16 sm:py-10 grid-bg">
      <Link to="/" data-testid="back-to-home-btn" className="fixed top-3 left-3 sm:top-6 sm:left-6 z-10 inline-flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl glass hover:bg-white/[0.08] text-xs sm:text-sm text-zinc-300 hover:text-white transition-colors">
        <ArrowLeft size={12} className="sm:w-3.5 sm:h-3.5"/> Back to Home
      </Link>
      <div className="w-full max-w-sm sm:max-w-md glass-strong rounded-2xl sm:rounded-3xl p-5 sm:p-8 shadow-2xl animate-fade-up">
        <Link to="/" className="flex items-center gap-2 mb-4 sm:mb-6">
          <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center">
            <Sparkles size={16} className="sm:w-[18px] sm:h-[18px]"/>
          </div>
          <div className="font-bold text-base sm:text-lg" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>HumanizeAI</div>
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Welcome back</h1>
        <p className="text-xs sm:text-sm text-zinc-400 mt-1">Sign in to continue humanizing.</p>

        <form onSubmit={submit} className="mt-5 sm:mt-6 space-y-3.5 sm:space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Email</Label>
            <Input data-testid="login-email" value={email} onChange={e=>setE(e.target.value)} type="email" required className="mt-1.5"/>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-zinc-400">Password</Label>
            <div className="relative mt-1.5">
              <Input data-testid="login-password" value={pw} onChange={e=>setPw(e.target.value)} type={show?"text":"password"} required/>
              <button type="button" onClick={()=>setShow(s=>!s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400">
                {show?<EyeOff size={16}/>:<Eye size={16}/>}
              </button>
            </div>
            <Link to="/forgot-password" className="text-xs text-indigo-300 hover:underline mt-2 inline-block">Forgot password?</Link>
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-indigo-600 hover:bg-indigo-700 glow-primary h-10 sm:h-11" data-testid="login-submit-btn">
            {loading ? <Loader2 className="animate-spin" size={16}/> : "Sign in"}
          </Button>
        </form>

        <div className="my-4 sm:my-5 flex items-center gap-3 text-xs text-zinc-500">
          <div className="flex-1 h-px bg-white/[0.08]"/>OR<div className="flex-1 h-px bg-white/[0.08]"/>
        </div>
        <Button onClick={google} variant="outline" className="w-full glass h-10 sm:h-11" data-testid="login-google-btn">
          Continue with Google
        </Button>

        <div className="mt-5 sm:mt-6 text-xs sm:text-sm text-zinc-400 text-center">
          New here? <Link to="/register" className="text-indigo-300 hover:underline">Create account</Link>
        </div>
      </div>
    </div>
  );
}
